import { setOutlineSelectedObjects } from "../../../core/pipeline/elements/outlineSelection";
import type { Engine } from "../../../engine/Engine";
import { getEventPixeslOnElement } from "../../../event/EventUtil";
import type { SugarModel } from "../../../loader/SugarModel";
import { SugarModelConfigurator } from "../../../loader/SugarModelConfigurator";
import type { FloatMenu } from "../../../core/ui-kit/FloatMenu";
import type { ConfiguratorController } from "../SugarConfiguratorController";

export class ConfiguratorSelectionController {
  selectedModel?: SugarModel;
  private engine?: Engine;
  private bindElement?: HTMLElement;
  private floatMenu?: FloatMenu;
  private floatMenuShowed = false;
  /** Incremental rotate delta tracker — same as DesignerOverlay. */
  private prevRotateTeta = 0;

  constructor(
    private readonly onSelectionChange: (model: SugarModel | undefined) => void,
    private readonly onRefresh: () => void,
    private readonly getController: () => ConfiguratorController | undefined,
  ) {}

  bind(engine: Engine, bindElement: HTMLElement): void {
    if (this.engine === engine && this.bindElement === bindElement) return;
    this.dispose();
    this.engine = engine;
    this.bindElement = bindElement;
    engine.addEventListener("model-selected", this.onModelSelected);
    engine.addEventListener("model-deselected", this.onModelDeselected);
    bindElement.addEventListener("click", this.onBindElementClick);
  }

  dispose(): void {
    this.syncOutlineSelection(undefined);
    this.engine?.removeEventListener("model-selected", this.onModelSelected);
    this.engine?.removeEventListener("model-deselected", this.onModelDeselected);
    this.bindElement?.removeEventListener("click", this.onBindElementClick);
    this.removeFloatMenu();
    this.engine = undefined;
    this.bindElement = undefined;
  }

  private syncOutlineSelection(model: SugarModel | undefined): void {
    setOutlineSelectedObjects(this.engine, model ? [model] : []);
  }

  private ensureFloatMenu(): FloatMenu {
    if (this.floatMenu) return this.floatMenu;
    this.floatMenu = document.createElement("float-menu") as FloatMenu;
    this.floatMenu.addEventListener("delete", this.onFloatDelete);
    this.floatMenu.addEventListener("rotate-change", this.onFloatRotateChange);
    this.floatMenu.addEventListener("rotate-end", this.onFloatRotateEnd);
    return this.floatMenu;
  }

  private readonly onFloatDelete = (): void => {
    this.getController()?.onDeleteOccured();
  };

  private readonly onFloatRotateChange = (event: Event): void => {
    const model = this.selectedModel ?? this.getController()?.selectedModel;
    if (!model) return;
    const progressTeta = (event as CustomEvent<{ progressTeta: number }>).detail
      .progressTeta;
    const delta = progressTeta - this.prevRotateTeta;
    model.rotateY(-delta);
    this.prevRotateTeta = progressTeta;
  };

  private readonly onFloatRotateEnd = (): void => {
    this.prevRotateTeta = 0;
    const model = this.selectedModel ?? this.getController()?.selectedModel;
    model?.updateMatrixWorld(true);
    // Direct commit — more reliable than engine event wiring.
    this.getController()?.snapshot("Rotate module");
    this.engine?.dispatchCustomEvent("configurator-rotation-committed");
  };

  private readonly onModelSelected = (event: Event): void => {
    this.selectedModel = (event as CustomEvent<SugarModel>).detail;
    this.prevRotateTeta = 0;
    this.syncOutlineSelection(this.selectedModel);
    this.onSelectionChange(this.selectedModel);
    this.onRefresh();
    if (!this.selectedModel) return;
    const menu = this.ensureFloatMenu();
    const configurable = this.selectedModel instanceof SugarModelConfigurator;
    menu.setAttribute("rotation", String(configurable));
    menu.setAttribute("delete", "true");
    menu.setAttribute("replace", String(!configurable));
    this.engine?.canvas?.parentElement?.append(menu);
  };

  private readonly onModelDeselected = (): void => {
    this.selectedModel = undefined;
    this.prevRotateTeta = 0;
    this.syncOutlineSelection(undefined);
    this.onSelectionChange(undefined);
    this.onRefresh();
    this.removeFloatMenu();
  };

  private readonly onBindElementClick = (
    event: MouseEvent | TouchEvent,
  ): void => {
    if (!this.floatMenu || this.floatMenuShowed) return;
    const { x, y } = getEventPixeslOnElement(event);
    const { width, height } = this.floatMenu.getBoundingClientRect();
    this.floatMenu.style.left = `${x - width * 0.5}px`;
    this.floatMenu.style.top = `${y - height * 0.5}px`;
    this.floatMenuShowed = true;
  };

  private removeFloatMenu(): void {
    if (this.floatMenu) {
      this.floatMenu.removeEventListener("delete", this.onFloatDelete);
      this.floatMenu.removeEventListener(
        "rotate-change",
        this.onFloatRotateChange,
      );
      this.floatMenu.removeEventListener("rotate-end", this.onFloatRotateEnd);
      this.floatMenu.remove();
    }
    this.floatMenu = undefined;
    this.floatMenuShowed = false;
  }
}
